import React from 'react'
import styled from 'styled-components'

import { useMediaQueryContext, useModalContext } from '@fourthwall/components'
import { useOnceInView, useTagManager } from '@hooks'

import { useRecommendationsDismiss } from '../../../Recommendations/hooks/useRecommendationsDismiss'
import { useCartAbandonmentEmailPromoRecommendation } from '../../../Recommendations/modules/CartAbandonmentEmailPromoRecommendation'
import type { MiniRecommendationSlideProps } from '../../MiniRecommendations.type'
import * as Carousel from '../../components/Carousel/Carousel'
import { MiniRecommendationDismissButton } from '../../components/MiniRecommendationDismissButton/MiniRecommendationDismissButton'
import { ConfirmEmailPromoCodeModal } from './ConfirmEmailPromoCodeModal/ConfirmEmailPromoCodeModal'

const ID = 'abandonmentDiscount' as const

const CarouselItem = styled(Carousel.Item)`
  background-color: rgba(0, 156, 224, 0.15);

  &:hover {
    background-color: rgba(0, 156, 224, 0.25);
  }
`

export const AbandonmentDiscountSlide = ({
  'data-role': dataRole,
  resetSlider,
}: MiniRecommendationSlideProps) => {
  const { open, close } = useModalContext()
  const { tablet } = useMediaQueryContext()
  const tagManager = useTagManager()
  const { recommendationsDismiss } = useRecommendationsDismiss()
  const { cartAbandonmentEmailPromoRecommendation, includePromoCode } =
    useCartAbandonmentEmailPromoRecommendation()
  const viewRef = useOnceInView(() => {
    tagManager.trigger('mini_recommendation_view', {
      type: ID,
    })
  })

  if (
    !cartAbandonmentEmailPromoRecommendation.enabled ||
    !recommendationsDismiss.promotions.visible
  ) {
    return null
  }

  const handleConfirmEmailPromoCode = () => {
    open(ConfirmEmailPromoCodeModal, {
      onConfirm: async () => {
        tagManager.trigger('mini_recommendation_click', {
          type: ID,
        })
        await includePromoCode()
        close()
        setTimeout(() => {
          resetSlider()
        }, 0)
      },
    })
  }

  return (
    <CarouselItem
      ref={viewRef}
      data-role={dataRole}
      data-analytics={ID}
      onClick={handleConfirmEmailPromoCode}
    >
      <Carousel.ContentLeft>
        <Carousel.ItemEmote>📨</Carousel.ItemEmote>

        {!tablet && <Carousel.ItemText> Add 5% off for abandoned carts</Carousel.ItemText>}
        {tablet && (
          <Carousel.ItemText>Tip: Offer 5% off in checkout abandonment emails</Carousel.ItemText>
        )}
      </Carousel.ContentLeft>

      {recommendationsDismiss.promotions.visible && (
        <MiniRecommendationDismissButton
          onDismiss={() => {
            recommendationsDismiss.promotions.dismiss()
            tagManager.trigger('mini_recommendation_dismiss', {
              type: ID,
            })
            setTimeout(() => {
              resetSlider()
            }, 0)
          }}
        />
      )}
    </CarouselItem>
  )
}
